import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation, withInMemoryScrolling } from '@angular/router';
import { provideFuse } from '@fuse';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideFCM } from 'app/core/fcm/fcm.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { provideToastr } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

// French translations for Material Paginator
function getFrenchPaginatorIntl() {
    const paginatorIntl = new MatPaginatorIntl();

    paginatorIntl.itemsPerPageLabel = 'Éléments par page :';
    paginatorIntl.nextPageLabel = 'Page suivante';
    paginatorIntl.previousPageLabel = 'Page précédente';
    paginatorIntl.firstPageLabel = 'Première page';
    paginatorIntl.lastPageLabel = 'Dernière page';
    paginatorIntl.getRangeLabel = (page: number, pageSize: number, length: number) => {
        if (length === 0 || pageSize === 0) {
            return `0 sur ${length}`;
        }
        length = Math.max(length, 0);
        const startIndex = page * pageSize;
        const endIndex = startIndex < length ?
            Math.min(startIndex + pageSize, length) :
            startIndex + pageSize;
        return `${startIndex + 1} - ${endIndex} sur ${length}`;
    };

    return paginatorIntl;
}

// Errors known to come from a fragile lazy-chunk load on cold app launch (seen on iOS/WKWebView):
// a DI token comes back undefined, or module evaluation order causes a recursive blowup.
// Neither is recoverable in-place — the safest thing is to let the user explicitly reload
// rather than auto-reloading (which can loop or just trade one crash for another).
function isKnownFatalChunkError(error: any): boolean {
    const message = typeof error?.message === 'string' ? error.message : '';
    return message.includes('token must be defined') || error?.name === 'RangeError';
}

// Raw DOM, deliberately not an Angular component: if Angular's own rendering is what just
// crashed, we can't trust change detection to render a recovery UI. This must work standalone.
function showRecoveryScreen(): void {
    if (document.getElementById('fatal-error-recovery')) {
        return;
    }
    const overlay = document.createElement('div');
    overlay.id = 'fatal-error-recovery';
    overlay.setAttribute('style', [
        'position:fixed', 'inset:0', 'z-index:2147483647',
        'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
        'gap:16px', 'padding:24px', 'text-align:center',
        'background:#111827', 'color:#fff', 'font-family:sans-serif'
    ].join(';'));
    overlay.innerHTML = `
        <div style="font-size:18px;">Un problème est survenu au chargement.</div>
        <div style="font-size:14px;opacity:0.7;">Appuyez pour réessayer.</div>
        <button id="fatal-error-retry" style="margin-top:8px;padding:10px 24px;border-radius:8px;border:none;background:#2563eb;color:#fff;font-size:16px;">Réessayer</button>
    `;
    document.body.appendChild(overlay);
    document.getElementById('fatal-error-retry')?.addEventListener('click', () => window.location.reload());
}

// Logs the real message/stack of uncaught errors instead of Angular's default
// `console.error('ERROR', error)`, which prints as an empty object on native
// platforms because Error.message/stack aren't enumerable own properties.
class VerboseErrorHandler implements ErrorHandler {
    handleError(error: any): void {
        console.error(
            'Uncaught error:',
            error?.message || error,
            '| name:', error?.name,
            '| stack:', error?.stack
        );

        if (isKnownFatalChunkError(error)) {
            showRecoveryScreen();
        }
    }
}

export const appConfig: ApplicationConfig = {
    providers: [
        { provide: ErrorHandler, useClass: VerboseErrorHandler },
        provideAnimations(),
        provideToastr(),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withHashLocation()
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        // Material Paginator French translations
        {
            provide: MatPaginatorIntl,
            useFactory: getFrenchPaginatorIntl,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            translocoService.setActiveLang(defaultLang);

            return firstValueFrom(translocoService.load(defaultLang));
        }),

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFCM(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        // Provider Ionic
        provideIonicAngular(),
    ],
};
